import {
  getSavedFilters,
  syncSavedFilters,
  mergeFiltersWithUrl,
  EXCLUDED_FACET_IDS,
} from "./filterSessionStorage";
import { normalizeRefineParameter } from "../helpers/normalizeRefineParameter";
import { queryStringToObject } from "../helpers/queryStringToObject";
import sdkProvider from "./sdkProvider";

export const EXPERIMENT_KEY = "plp-persist-filters";

let featuresSdk;

export const initSaveFiltersExperiment = async () => {
  featuresSdk = await sdkProvider("features");
  await featuresSdk.onReady();
};

export const isSaveFiltersExperimentEnabled = () => {
  const feature = featuresSdk?.getFeature(EXPERIMENT_KEY);

  return Boolean(feature?.isEnabled());
};

export const getRestoredFiltersForNavigation = (location) => {
  if (!isSaveFiltersExperimentEnabled()) {
    return null;
  }

  const query = location.query || queryStringToObject(location.search || "");

  const savedFilters = getSavedFilters();
  if (!savedFilters) {
    return null;
  }

  const merged = mergeFiltersWithUrl(query, savedFilters);

  const hasNewFilters =
    normalizeRefineParameter({ refine: merged.refine }) !==
    normalizeRefineParameter({ refine: query.refine });

  if (!hasNewFilters) {
    return null;
  }

  return merged;
};

export const saveCurrentFiltersFromLocation = (location, facets = []) => {
  if (!isSaveFiltersExperimentEnabled()) {
    return;
  }

  const query = location.query || queryStringToObject(location.search || "");

  // Navigation between listing pages must only persist the filters that are
  // applied on the page just loaded. It must never delete carry-over filters
  // saved from other categories, otherwise a value that is not applicable here
  // would be lost. Removals are handled exclusively by the apply/clearAll
  // interaction thunks.
  syncSavedFilters({ refine: query.refine, facets, removeUnapplied: false });
};

export const safeDecodeRefineParam = (encoded) =>
  encoded.replace(/%3A/gi, ":").replace(/%7C/gi, "|");

export const buildLocationWithSearch = (location, search) => ({
  ...location,
  search,
  query: queryStringToObject(search),
});

export const buildLocationWithRestoredFilters = (location, restoredFilters) => {
  const searchParams = new URLSearchParams(location.search || "");
  searchParams.delete("refine");

  const baseString = searchParams.toString();
  const refine = restoredFilters?.refine;
  const refineString = refine
    ? `&${safeDecodeRefineParam(new URLSearchParams({ refine }).toString())}`
    : "";

  return buildLocationWithSearch(location, `?${baseString}${refineString}`);
};

const getAllowedValuesByFacetId = (facets, useAllOfferedValues) => {
  const selectedValuesByFacetId = new Map();

  facets.forEach((facet) => {
    const selectedValues = (facet.facetValues ?? [])
      .filter(
        (facetValue) =>
          (useAllOfferedValues || facetValue.isSelected === true) &&
          facetValue.count !== 0,
      )
      .map((facetValue) => String(facetValue.id));

    selectedValuesByFacetId.set(facet.id, new Set(selectedValues));
  });

  return selectedValuesByFacetId;
};

const keepAllowedRefineParts = (refine, allowedValuesByFacetId) =>
  refine.split("|").reduce((parts, part) => {
    const colonIndex = part.indexOf(":");
    const facetId = colonIndex === -1 ? part : part.slice(0, colonIndex);

    if (EXCLUDED_FACET_IDS.has(facetId)) {
      parts.push(part);

      return parts;
    }

    const allowedValues = allowedValuesByFacetId.get(facetId);
    if (!allowedValues) {
      return parts;
    }

    const requestedValues =
      colonIndex === -1 ? [] : part.slice(colonIndex + 1).split(",");
    const keptValues = requestedValues.filter((value) =>
      allowedValues.has(value),
    );

    if (keptValues.length) {
      parts.push(`${facetId}:${keptValues.join(",")}`);
    }

    return parts;
  }, []);

export const buildValidatedRestoredLocation = (location, facets = []) => {
  const savedFilters = getSavedFilters();
  if (!savedFilters?.refine) {
    return null;
  }

  const applicableParts = keepAllowedRefineParts(
    savedFilters.refine,
    getAllowedValuesByFacetId(facets, true),
  );
  const query = location.query || queryStringToObject(location.search || "");
  const merged = mergeFiltersWithUrl(query, {
    refine: applicableParts.join("|"),
  });

  if (
    normalizeRefineParameter({ refine: merged.refine }) ===
    normalizeRefineParameter({ refine: query.refine })
  ) {
    return null;
  }

  return buildLocationWithRestoredFilters(location, merged);
};

// Rebuilds the refine param from the values the visited PLP actually applied.
// The search API ignores unsupported facets and unsupported values, so the
// fetched facets reflect exactly what was applied: a supported value comes back
// marked selected with results, while unsupported facets/values are absent,
// unselected, or have a zero count.
// Keeping only the selected values drops stale/unsupported filters (and never
// leaves an empty `facetId:` group) without needing a refetch. Excluded facets
// such as price and floor are always preserved verbatim.
export const buildReconciledSearch = (
  search,
  facets = [],
  useAllOfferedValues = false,
) => {
  const searchParams = new URLSearchParams(search || "");
  const refine = searchParams.get("refine");

  if (!refine) {
    return search;
  }

  const selectedValuesByFacetId = getAllowedValuesByFacetId(
    facets,
    useAllOfferedValues,
  );
  const keptParts = keepAllowedRefineParts(refine, selectedValuesByFacetId);

  const nextRefine = keptParts.join("|");
  if (nextRefine === refine) {
    return search;
  }

  searchParams.delete("refine");

  const baseString = searchParams.toString();
  const refineString = nextRefine
    ? `&${safeDecodeRefineParam(
        new URLSearchParams({ refine: nextRefine }).toString(),
      )}`
    : "";

  return `?${baseString}${refineString}`;
};
